<script lang="ts" setup>
import type { Section } from '~/types'
import { getDistanceBetweenPoints } from '~/utils/distance'

interface Props extends Pick<Section, 'collection' | 'title' | 'description'> { }
const props = defineProps<Props>()
const route = useRoute()
const { userLocation, selectedDistance } = useSettings()

const collectionFull = GetCollection(props.collection)

const sortOptions = ref<any>({
  sortBy: '',
  sortDesc: false,
  page: 1,
  perPage: 100,
})

const sortableFieldOptions = ref([])
const searchQuery = ref('')

onMounted(async () => {
  sortableFieldOptions.value = collectionFull?.pub?.sortableFields ?? []
  if (sortOptions.value.sortBy === '') {
    const { sortDesc, sortBy } = getInitialSortOptions()
    sortOptions.value.sortDesc = sortDesc
    sortOptions.value.sortBy = sortBy
  }

  if (route.query.q) {
    searchQuery.value = route.query.q as string
  }
})

function getInitialSortOptions() {
  const sortOp = { sortBy: 'id', sortDesc: false }
  if (collectionFull?.pub?.defaultSortField) {
    sortOp.sortDesc = collectionFull?.pub?.defaultSortField?.startsWith('-') ?? false
    sortOp.sortBy = collectionFull?.pub?.defaultSortField?.replace(/^-/, '') ?? 'id'
  }
  return sortOp
}

const debouncedSearch = refThrottled(searchQuery, 500)

const pb = useNuxtApp().$pb

async function getAdditionalInformation() {
  const col = GetCollection(props.collection)
  const additionalSortOptions = {}
  const filterableRelations = {}
  let filtersString = ''
  if (col?.pub?.filterableRelations?.length) {
    for (let i = 0; i < col.pub.filterableRelations.length; i++) {
      const relation = col.pub.filterableRelations[i]
      const relationFull = GetCollection(relation)
      if (!relationFull) {
        continue
      }
      const colString = relationFull?.pub?.collection ?? relationFull?.collection
      const { items = [] } = await pb.collection(colString).getList(1, 999, { sort: relationFull.pub?.defaultSortField, fields: relationFull.pub?.defaultFields ?? '*' })
      if (items?.length) {
        filterableRelations[relationFull.collection] = items
      }
      if (sortOptions.value?.[relationFull.collection]?.length) {
        if (filtersString !== '') {
          filtersString += ' && '
        }
        filtersString += `(${relationFull.collection} ~ "${sortOptions.value?.[relationFull.collection].join(`") || (${relationFull.collection} ~ "`)}")`
      }
      else {
        additionalSortOptions[relation] = []
      }
    }
  }

  return { filtersString, filterableRelations, additionalSortOptions }
}

/**
 * 1. Helper: Construct the PocketBase Sort String
 */
const getSortString = (options) => {
  const initial = getInitialSortOptions()

  // Use provided sortBy or fallback
  const rawSortBy = options.sortBy || initial.sortBy

  // Logic: if sortBy is empty, we use the initial toggle.
  // If sortBy is provided, we use the current toggle.
  const isDescending = options.sortBy === '' ? initial.sortDesc : options.sortDesc

  return rawSortBy
    .split(',')
    .map((field) => {
      // 1. Remove any existing leading dash to avoid double-dashing
      const cleanField = field.trim().replace(/^-/, '')

      // 2. Apply dash only if isDescending is true
      return isDescending ? `-${cleanField}` : cleanField
    })
    .join(',')
}

/**
 * 2. Helper: Build the Filter String for PocketBase
 */
const buildPbFilter = (pb, col, baseFilters, query) => {
  const parts = [baseFilters].filter(Boolean)

  if (query && query.length > 2) {
    const searchFields = col?.pub?.searchFields || []
    const searchCondition = `(${searchFields.join(' ?~ {:q} || ')} ?~ {:q})`
    parts.push(searchCondition)
  }

  return pb.filter(parts.join(' && '), { ...sortOptions.value, q: query })
}

/**
 * 3. Helper: Client-side Distance Filtering
 */
const filterByDistance = (items, location, maxDistance) => {
  if (maxDistance === undefined || !location.lat || !location.lng) return items

  return items.filter(({ map }) => {
    if (!map?.lat || !map?.lng) return true
    const distance = getDistanceBetweenPoints(location, { lat: map.lat, lng: map.lng })
    return distance <= (maxDistance ?? Infinity)
  })
}

/**
 * MAIN ASYNC DATA CALL
 */
const { data, status, refresh } = await useAsyncData(
  `directories-${props.collection}-${sortOptions.value.perPage}`,
  async () => {
    const pb = useNuxtApp().$pb
    const col = GetCollection(props.collection)
    const { filtersString, filterableRelations, additionalSortOptions } = await getAdditionalInformation()

    // Prepare Query Params
    const sort = getSortString(sortOptions.value)
    const filter = buildPbFilter(pb, col, filtersString, searchQuery.value)
    const collectionName = col?.pub?.collection ?? col?.collection

    // Execution
    const result = await pb.collection(collectionName).getList(
      sortOptions.value.page,
      sortOptions.value.perPage,
      {
        filter,
        sort,
        fields: col?.pub?.defaultFields,
        expand: col?.pub?.expand,
      },
    )

    // Post-Processing
    result.items = filterByDistance(result.items, userLocation.value, selectedDistance.value)

    return {
      records: result,
      filterableRelations,
      additionalSortOptions,
    }
  },
  {
    server: true,
    watch: [debouncedSearch, sortOptions.value, selectedDistance, userLocation],
  },
)

watchOnce(() => data.value?.filterableRelations, (val) => {
  if (val) {
    for (const key in val) {
      sortOptions.value[key] = []
    }
  }
}, { immediate: true })

const { siteUrl } = useRuntimeConfig().public
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  'url': siteUrl,
  'potentialAction': {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${siteUrl}?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
}

useHeadSafe({
  script: [
    {
      type: 'application/ld-json',
      children: JSON.stringify(jsonLd),
    },
  ],
})
</script>

<template>
  <div class="relative">
    <div
      v-if="!collectionFull?.pub?.hideMap"
      class="relative"
    >
      <LocationMap
        class="w-full"
        :items="data?.records?.items"
        :center="userLocation"
        :distance="selectedDistance"
        :zoom="10"
      />
      <SearchCollection
        v-show="collectionFull?.title"
        v-model:query="searchQuery"
        :status="status"
        :search-collection="collectionFull?.collection"
        :placeholder="`Search for ${collectionFull?.title} by Location...`"
        class="bg-primary-700 lg:bg-transparent lg:absolute left-0 right-0 lg:-bottom-10 px-6 py-4 lg:py-3 z-20"
      />
    </div>

    <div
      v-if="!collectionFull?.pub?.hideMap"
      class="w-full bg-primary-700 hidden lg:block lg:h-12"
    />
    <div
      v-if="collectionFull?.pub?.hideMap"
      class="absolute top-0 left-0 right-0"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 320"
      >
        <path
          class="fill-primary-500"
          fill-opacity="1"
          d="M0,256L48,250.7C96,245,192,235,288,208C384,181,480,139,576,128C672,117,768,139,864,149.3C960,160,1056,160,1152,160C1248,160,1344,160,1392,160L1440,160L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
        />
      </svg>
    </div>
    <HeadingText
      v-if="props.title"
      tag="h1"
      class="text-center pt-12 lg:pt-24 text-pink z-10 relative"
      :class="[collectionFull?.pub?.hideMap ? 'mt-20' : '']"
    >
      {{ props.title }}
    </HeadingText>
    <div class="flex w-full flex-wrap gap-x-3 gap-y-3 px-6 justify-between pb-6 max-w-7xl mx-auto mt-12 lg:mt-20">
      <template
        v-for="(relation, k) in data?.filterableRelations"
        :key="k"
      >
        <USelectMenu
          v-model="sortOptions[k]"
          :options="relation"
          option-attribute="name"
          value-attribute="id"
          class="w-full md:w-64"
          multiple
          :placeholder="`Filter ${k}`"
          searchable
          :ui="{ base: 'capitalize', rounded: 'rounded-full' }"
          size="lg"
        />
      </template>
      <div
        v-if="sortableFieldOptions?.length"
        class="flex flex-wrap gap-3 ml-auto"
      >
        <USelectMenu
          v-model="sortOptions.sortBy"
          icon="ri:list-ordered"
          :options="sortableFieldOptions"
          size="lg"
          value-attribute="value"
          option-attribute="label"
          :ui="{ rounded: 'rounded-full' }"
        />
        <Transition
          name="fade"
          mode="out-in"
        >
          <UButton
            v-if="sortOptions.sortDesc"
            color="gray"
            aria-label="sort descending"
            icon="ri:sort-alphabet-desc"
            @click="sortOptions.sortDesc = !sortOptions.sortDesc"
          />
          <UButton
            v-else
            color="gray"
            aria-label="sort ascending"
            icon="ri:sort-alphabet-asc"
            @click="sortOptions.sortDesc = !sortOptions.sortDesc"
          />
        </Transition>
      </div>
    </div>

    <div
      class="flex flex-col  mx-auto relative px-6"
      :class="[collection === 'gbfReviews' ? '' : 'max-w-7xl']"
    >
      <transition
        name="fade"
        mode="out-in"
      >
        <Loading
          v-if="status === 'pending'"
          class="absolute inset-0 backdrop-blur-md"
        />
      </transition>

      <transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="!data?.records?.items?.length"
          class="flex justify-center py-24"
        >
          <HeadingText
            tag="h3"
            class="text-primary-700"
          >
            No {{ collectionFull?.title }} Found
          </HeadingText>
        </div>
        <div
          v-else-if="collection !== 'gbfReviews'"
          class="gap-6 flex flex-col"
          :class="[collection === 'gbfReviews' ? 'grid md:grid-cols-2 xl:grid-cols-3 gap-6' : 'flex flex-col']"
        >
          <div
            v-for="(item) in data?.records?.items"
            :key="item.id"
            data-allow-mismatch
          >
            <component
              :is="collectionFull?.pub?.cardComponent"
              :data="item"
            />
          </div>
        </div>
        <div
          v-else-if="data?.records?.items?.length"
          class="grid md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch"
        >
          <div
            v-for="(item) in data?.records?.items"
            :key="item.id"
            data-allow-mismatch
            class="h-full flex flex-col"
          >
            <component
              :is="collectionFull?.pub?.cardComponent"
              :data="item"
              card-class="h-full"
              class="flex-1"
            />
          </div>
        </div>
      </transition>
    </div>

    <!-- <ContactForm class="mx-auto max-w-7xl px-6 py-32 lg:px-10" /> -->
  </div>
</template>
